/**
 * BookingCalendar Component
 * Calendly-like booking interface
 */

import { useState, useEffect } from 'react';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { Calendar, Clock, User, Mail, Phone, Video, CheckCircle, Copy } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useBooking } from '@/hooks/useBooking';
import { BOOKING_TRANSLATIONS, TimeSlot, BookingConfirmation } from '@/types/booking.types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarUI } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface BookingCalendarProps {
  hostId: string;
  hostName?: string;
  dealId?: string;
  defaultTitle?: string;
  onBooked?: (confirmation: BookingConfirmation) => void;
}

export function BookingCalendar({
  hostId,
  hostName = 'Host',
  dealId,
  defaultTitle,
  onBooked,
}: BookingCalendarProps) {
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = BOOKING_TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];
  const locale = language === 'fr' ? fr : enUS;

  const { loading, availability, getAvailableSlots, fetchMeetingsForDate, bookMeeting } = useBooking(hostId);

  const [step, setStep] = useState<'date' | 'time' | 'details' | 'confirmed'>('date');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<string | undefined>();
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    title: defaultTitle || '',
  });

  // Fetch slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchMeetingsForDate(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate, fetchMeetingsForDate]);

  // Update available slots when date or availability changes
  useEffect(() => {
    if (selectedDate && availability) {
      const slots = getAvailableSlots(selectedDate);
      setAvailableSlots(slots);
    }
  }, [selectedDate, availability, getAvailableSlots]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTime(undefined);
    if (date) {
      setStep('time');
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setStep('details');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || !formData.name || !formData.email) return;

    const result = await bookMeeting({
      hostId,
      guestName: formData.name,
      guestEmail: formData.email,
      guestPhone: formData.phone,
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: selectedTime,
      duration: availability?.slotDuration || 30,
      title: formData.title || `Meeting with ${formData.name}`,
      description: formData.notes,
      dealId,
    });

    if (result) {
      setConfirmation(result);
      setStep('confirmed');
      onBooked?.(result);
    }
  };

  const copyMeetingLink = () => {
    if (confirmation?.meetingUrl) {
      navigator.clipboard.writeText(confirmation.meetingUrl);
      toast({ title: t.linkCopied });
    }
  };

  const disabledDays = (date: Date) => {
    const today = startOfToday();
    if (isBefore(date, today)) return true;
    if (!availability) return false;
    const dayOfWeek = date.getDay();
    return !availability.slots.some((s) => s.dayOfWeek === dayOfWeek);
  };

  if (!availability) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto overflow-hidden">
      <CardHeader className="bg-primary text-primary-foreground">
        <CardTitle className="flex items-center gap-2">
          <Video className="w-5 h-5" />
          {t.title}
        </CardTitle>
        <CardDescription className="text-primary-foreground/80">
          {t.meetingWith} {hostName}
        </CardDescription>
      </CardHeader>

      <CardContent className="p-6">
        {step === 'confirmed' && confirmation ? (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">{t.confirmTitle}</h3>
              <p className="text-muted-foreground mt-1">{t.confirmMessage}</p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-left">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t.dateTime}:</span>
                <span className="font-medium">
                  {format(new Date(confirmation.date), 'PPP', { locale })} {confirmation.time}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t.duration}:</span>
                <span className="font-medium">{confirmation.duration} {t.minutes}</span>
              </div>
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t.joinLink}:</span>
                <Button variant="outline" size="sm" onClick={copyMeetingLink}>
                  <Copy className="w-3 h-3 mr-1" />
                  {t.copyLink}
                </Button>
              </div>
            </div>

            <Button onClick={() => window.open(confirmation.meetingUrl, '_blank')} className="w-full">
              <Video className="w-4 h-4 mr-2" />
              {t.joinLink}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Calendar Section */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t.selectDate}
              </h4>
              <CalendarUI
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={disabledDays}
                locale={locale}
                className="rounded-md border pointer-events-auto"
                fromDate={new Date()}
                toDate={addDays(new Date(), 60)}
              />
            </div>

            {/* Time Slots / Form Section */}
            <div>
              {step === 'date' && (
                <div className="h-full flex items-center justify-center">
                  <p className="text-muted-foreground text-center">{t.selectDateFirst}</p>
                </div>
              )}

              {step === 'time' && selectedDate && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t.selectTime}
                  </h4>
                  {availableSlots.filter((s) => s.available).length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t.noSlots}</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-2">
                      {availableSlots
                        .filter((s) => s.available)
                        .map((slot) => (
                          <Button
                            key={slot.time}
                            variant="outline"
                            size="sm"
                            onClick={() => handleTimeSelect(slot.time)}
                            className={cn(
                              'justify-center',
                              selectedTime === slot.time && 'border-primary bg-primary/10'
                            )}
                          >
                            {slot.time}
                          </Button>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {step === 'details' && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t.yourDetails}
                  </h4>

                  <div className="space-y-2">
                    <Label htmlFor="name">{t.name} *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-9"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t.email} *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-9"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t.phone}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">{t.notes}</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setStep('time')}>
                      ←
                    </Button>
                    <Button type="submit" className="flex-1" disabled={loading}>
                      {loading ? t.booking : t.bookMeeting}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
